import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LineChart, Line, CartesianGrid } from 'recharts';
import { bmoApi } from '../services/api';
import { colors } from '../theme';
import { Card, StatCard, SectionHeader, Spinner, AlertBanner, EmptyState } from '../components/common';

const CAT_COLORS = { 'Cat 1':'#F44336','Cat 2':'#F44336','Cat 3':'#F44336','Cat 4':'#FF9800','Cat 5':'#9C27B0','Cat 6':'#F44336','Cat 7':'#2196F3','Cat 8':'#607D8B','Cat 9':'#795548','Cat 10':'#9E9E9E' };
const PERIODS = [['month','This Month'],['30d','Last 30 Days'],['90d','Last 90 Days'],['year','This Year']];

export default function BMWPage() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [period,  setPeriod]  = useState('month');

  useEffect(() => {
    bmoApi.getBMWReport({ period })
      .then(setData)
      .catch((e) => setError(e?.message || 'Could not load BMW data.'))
      .finally(() => setLoading(false));
  }, [period]);

  if (loading) return <Spinner size={48} />;
  if (error)   return <AlertBanner type="error" message={error} />;

  const byCategory = data?.by_category || [];
  const byFacility = data?.by_facility || [];
  const totalWeight = byCategory.reduce((s, c) => s + (c.quantity || 0), 0).toFixed(2);

  return (
    <div>
      <div style={{ marginBottom:24, display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 style={{ margin:'0 0 4px', fontSize:24, fontWeight:700, color: colors.text.primary }}>🗑️ BMW Waste Report</h1>
          <p style={{ margin:0, fontSize:14, color: colors.text.secondary }}>District-wide biomedical waste disposal — CPCB BMW Rules 2016</p>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          {PERIODS.map(([key, lbl]) => (
            <button key={key} onClick={() => setPeriod(key)}
              style={{ padding:'6px 14px', borderRadius:99, border:'none', cursor:'pointer', fontSize:13, background: period === key ? colors.primary[700] : colors.neutral[100], color: period === key ? colors.white : colors.text.secondary }}>
              {lbl}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(160px, 1fr))', gap:16, marginBottom:24 }}>
        <StatCard icon="⚖️" label="Total (kg/L)"    value={totalWeight}              color={colors.primary[700]} />
        <StatCard icon="🏥" label="Facilities"       value={byFacility.length}         color={colors.success[700]} />
        <StatCard icon="📦" label="Categories"       value={byCategory.length}         color={colors.warning[700]} />
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
        <Card>
          <SectionHeader title="By Category (CPCB)" subtitle="Colour-coded waste streams" />
          {byCategory.length === 0
            ? <EmptyState icon="🗑️" title="No data" />
            : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={byCategory} layout="vertical">
                  <XAxis type="number" tick={{ fontSize:12 }} />
                  <YAxis dataKey="category_code" type="category" width={60} tick={{ fontSize:12 }} />
                  <Tooltip formatter={(v) => [`${v.toFixed(2)} kg/L`]} />
                  <Bar dataKey="quantity" radius={[0,4,4,0]}>
                    {byCategory.map((c, i) => <Cell key={i} fill={CAT_COLORS[c.category_code] || colors.neutral[400]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )
          }
        </Card>
        <Card>
          <SectionHeader title="By Facility" subtitle="Total BMW weight per facility" />
          {byFacility.length === 0
            ? <EmptyState icon="🏥" title="No data" />
            : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={byFacility}>
                  <XAxis dataKey="name" tick={{ fontSize:11 }} angle={-30} textAnchor="end" height={50} />
                  <YAxis tick={{ fontSize:12 }} />
                  <Tooltip formatter={(v) => [`${v.toFixed(2)} kg/L`]} />
                  <Bar dataKey="total" fill={colors.primary[500]} radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            )
          }
        </Card>
      </div>

      <Card>
        <div style={{ fontSize:13, color: colors.success[800], background: colors.success[50], border:`1px solid ${colors.success[100]}`, borderRadius:8, padding:'10px 14px' }}>
          ✅ Records maintained as per BMW (Management &amp; Handling) Rules 2016 and CPCB guidelines. Annual return to be submitted to SPCB/PCC.
        </div>
      </Card>
    </div>
  );
}
